import { useAuthStore } from '@/stores/auth';
import { isBoolean, isResponse } from '@/utils';

export const basePath = import.meta.env.VITE_API_URL || '/api/v1';
export const unauthorized = [401, 402, 403];

export const requestWrapper = {
  post: request('POST'),
  get: request('GET'),
  delete: request('DELETE'),
  direct: dFetch,
};

function dFetch(method: string, path: string, body?: object): Promise<Response> {
  const requestOptions: RequestInit = {};
  requestOptions.headers = authHeader(basePath + path);
  requestOptions.method = method;
  if (body != null) {
    requestOptions.body = JSON.stringify(body);
  }
  return fetch(basePath + path, requestOptions);
}

function request(method: string) {
  return async ({ path, body }: {
    path: string;
    body?: object;
  }): Promise<Response> => {
    const requestOptions: RequestInit = {};
    requestOptions.headers = authHeader(basePath + path);
    requestOptions.method = method;
    if (body != null) {
      requestOptions.body = JSON.stringify(body);
    }
    return handleResponse(fetch(basePath + path, requestOptions))
      .then(response => {
        if (isResponse(response)) {
          return response;
        }
        if (isBoolean(response) && response) {
          return request(method)({
            path: path,
            body: body ?? {}
          });
        }
        const res = response as Error;
        return Promise.reject(res);
      });
  };
}

// helper functions

function authHeader(url: string): HeadersInit {
  // return auth header with jwt if user is logged in and request is to the api url
  const authStore = useAuthStore();
  const isLoggedIn = !!authStore.access;
  const isApiUrl = url.startsWith(basePath);
  const header = {
    'Content-Type': 'application/json'
  };
  if (isLoggedIn && isApiUrl) {
    return Object.assign(header, {
      Authorization: `Bearer ${authStore.accessString}`
    });
  } else {
    return header;
  }
}

async function handleResponse(apiResponse: Promise<Response>): Promise<Response | Error | boolean> {
  return apiResponse.then(async (response: Response) => {
    if (response.ok) {
      return response;
    }
    if (!unauthorized.includes(response.status)) {
      return await parseErrorResponse(response);
    }
    const authStore = useAuthStore();
    return await authStore.tokenLogin()
      .then(() => {
        // authentication successful
        return Promise.resolve(true);
      })
      .catch(async () => {
        // auto logout if Unauthorized response returned from api
        authStore.logout().catch(console.log);
        return await parseErrorResponse(response);
      });
  }).catch((reason: Error) => {
    return Promise.reject(reason);
  });
}

async function parseErrorResponse(response: Response) {
  return response.text().then((text: string) => {
      const data = text && JSON.parse(text);
      return Error((data && data.msg) || response.statusText);
    }
  ).catch((e: Error) => {
    return e;
  });
}
